import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Languages, ArrowLeftRight, Copy, Check, Loader2,
  Upload, FileText, History, Trash2, X, ChevronDown,
  Mic, Volume2, Sparkles, StopCircle, Youtube, Scissors, Lock
} from 'lucide-react'
import client from '../api/client'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { useTokenStore } from '../store/tokenStore'

// ---- Types ----

interface Language {
  code: string
  name: string
}

interface HistoryItem {
  id: number
  source_text: string
  translated_text: string | null
  source_lang: string
  target_lang: string
  model_name: string | null
  file_name: string | null
  tokens_spent: number
  created_at: string
}

const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'auto', name: 'Автоопределение' },
  { code: 'russian', name: 'Русский' },
  { code: 'english', name: 'Английский' },
  { code: 'german', name: 'Немецкий' },
  { code: 'french', name: 'Французский' },
  { code: 'spanish', name: 'Испанский' },
  { code: 'italian', name: 'Итальянский' },
  { code: 'portuguese', name: 'Португальский' },
  { code: 'chinese', name: 'Китайский' },
  { code: 'japanese', name: 'Японский' },
  { code: 'korean', name: 'Корейский' },
  { code: 'arabic', name: 'Арабский' },
  { code: 'turkish', name: 'Турецкий' },
  { code: 'polish', name: 'Польский' },
  { code: 'ukrainian', name: 'Украинский' },
  { code: 'dutch', name: 'Нидерландский' },
]

const YOUTUBE_LANGS: Language[] = [
  { code: 'ru', name: 'Русский' },
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'ja', name: '日本語' },
  { code: 'zh', name: '中文' },
  { code: 'ko', name: '한국어' },
]

const LANG_EMOJIS: Record<string, string> = {
  auto: '🌐',
  russian: '🇷🇺',
  english: '🇬🇧',
  german: '🇩🇪',
  french: '🇫🇷',
  spanish: '🇪🇸',
  italian: '🇮🇹',
  portuguese: '🇵🇹',
  chinese: '🇨🇳',
  japanese: '🇯🇵',
  korean: '🇰🇷',
  arabic: '🇸🇦',
  turkish: '🇹🇷',
  polish: '🇵🇱',
  ukrainian: '🇺🇦',
  dutch: '🇳🇱',
}

const YT_LANG_OPTIONS = [
  { code: 'ru', name: 'Русский' },
  { code: 'en', name: 'Английский' },
  { code: 'de', name: 'Немецкий' },
  { code: 'fr', name: 'Французский' },
  { code: 'es', name: 'Испанский' },
]

// Тип модели с сервера
interface AiModelItem {
  id: number
  model_name: string
  display_name: string | null
  provider: string | null
  category: string
  is_paid: boolean
  icon_url: string | null
  is_active: boolean
  cost_per_million_usd: number | null
  sort_order: number | null
}

export default function Translator() {
  const { balance } = useTokenStore()
  const isPaidLocked = (balance ?? 0) < 1000
  const [activeTab, setActiveTab] = useState<'translate' | 'compress' | 'youtube'>('translate')

  const [sourceText, setSourceText] = useState('')
  const [translatedText, setTranslatedText] = useState('')
  const [sourceLang, setSourceLang] = useState('auto')
  const [targetLang, setTargetLang] = useState('english')
  const [detectedLang, setDetectedLang] = useState<string | null>(null)
  const [translating, setTranslating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showSourcePicker, setShowSourcePicker] = useState(false)
  const [showTargetPicker, setShowTargetPicker] = useState(false)
  const [showModelPicker, setShowModelPicker] = useState(false)
  const [selectedModel, setSelectedModel] = useState('')
  const [serverModels, setServerModels] = useState<AiModelItem[]>([])
  const [calculatedPrices, setCalculatedPrices] = useState<Record<string, {input: number, output: number}>>({})
  const [history, setHistory] = useState<HistoryItem[]>([])

  // Compress state
  const [compressText, setCompressText] = useState('')
  const [compressResult, setCompressResult] = useState('')
  const [compressing, setCompressing] = useState(false)
  const compressTextareaRef = useRef<HTMLTextAreaElement>(null)

  // YouTube state
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [youtubeResult, setYoutubeResult] = useState('')
  const [youtubeLoading, setYoutubeLoading] = useState(false)
  const [youtubeLang, setYoutubeLang] = useState('ru')
  const [showYoutubeLangPicker, setShowYoutubeLangPicker] = useState(false)

  // Форматирование цены из calculated-prices (синхронизировано с админкой)
  const formatPrice = useCallback((m: AiModelItem) => {
    if (!m.is_paid) return null
    const cp = calculatedPrices[m.model_name]
    if (cp) return `${cp.input}/${cp.output}`
    return null
  }, [calculatedPrices])

  // Отсортированные модели (FREE → 👑, по sort_order)
  const sortedModels = useMemo(() => {
    const free = serverModels.filter(m => !m.is_paid).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    const paid = serverModels.filter(m => m.is_paid).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    return [...free, ...paid]
  }, [serverModels])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: number; originalText?: string } | null>(null)
  const [listening, setListening] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [showVoicePicker, setShowVoicePicker] = useState(false)
  const [selectedVoiceUri, setSelectedVoiceUri] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const recognitionRef = useRef<any>(null)

  // ---- Voice input (Speech-to-Text) ----
  const handleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Голосовой ввод не поддерживается в вашем браузере')
      return
    }
    if (listening) {
      recognitionRef.current?.stop()
      setListening(false)
      return
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    recognition.lang = 'ru-RU'
    recognition.continuous = false
    recognition.interimResults = false

    recognition.onresult = (e: any) => {
      const text = e.results[0][0].transcript
      setSourceText((prev) => prev + text)
      setListening(false)
    }
    recognition.onerror = () => {
      setListening(false)
      toast.error('Ошибка распознавания речи')
    }
    recognition.onend = () => setListening(false)
    recognitionRef.current = recognition
    recognition.start()
    setListening(true)
  }

  // ---- Voice output (TTS через Edge TTS на бэкенде) ----
  const [availableVoices, setAvailableVoices] = useState<{name: string; locale: string; friendly_name: string}[]>([])

  // Загружаем список голосов с сервера
  useEffect(() => {
    client.get('/tts/voices').then(({data}) => {
      if (data?.voices) setAvailableVoices(data.voices)
    }).catch(() => {/* игнорим */})
  }, [])

  // Группируем голоса по локали
  const voicesGrouped = useMemo(() => {
    const groups: Record<string, typeof availableVoices> = {}
    availableVoices.forEach(v => {
      const code = v.locale?.split('-')[0]?.toLowerCase() || 'xx'
      if (!groups[code]) groups[code] = []
      groups[code].push(v)
    })
    return groups
  }, [availableVoices])

  // Карта: наше имя языка → префикс локали
  const langToPrefix: Record<string, string> = {
    russian: 'ru', english: 'en', german: 'de', french: 'fr',
    spanish: 'es', italian: 'it', portuguese: 'pt', chinese: 'zh',
    japanese: 'ja', korean: 'ko', arabic: 'ar', turkish: 'tr',
    polish: 'pl', ukrainian: 'uk', dutch: 'nl',
  }

  const audioRef = useRef<HTMLAudioElement | null>(null)

  const speakText = useCallback(async (text: string, voiceName: string) => {
    if (!text) return
    // Если уже играет — останавливаем
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    setSpeaking(true)
    try {
      const resp = await client.post('/tts/synthesize', {
        text,
        voice: voiceName,
        rate: '+0%',
      }, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      const audio = new Audio(url)
      audioRef.current = audio
      audio.onended = () => { setSpeaking(false); audioRef.current = null; URL.revokeObjectURL(url) }
      audio.onerror = () => { setSpeaking(false); audioRef.current = null; URL.revokeObjectURL(url) }
      audio.play()
    } catch {
      setSpeaking(false)
      toast.error('Ошибка воспроизведения')
    }
  }, [])

  const handleSpeak = () => {
    if (!translatedText) return
    if (speaking) {
      // Останавливаем воспроизведение
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setSpeaking(false)
      return
    }
    speakText(translatedText, selectedVoiceUri || 'ru')
  }

  // ---- Загрузка моделей с сервера ----
  // ---- Загрузка моделей и цен с сервера ----
  useEffect(() => {
    const load = async () => {
      try {
        const [modelsRes, pricesRes] = await Promise.all([
          client.get('/models/ai', { params: { category: 'chat' } }),
          client.get('/admin/ai-models/calculated-prices').catch(() => ({data: {}})),
        ])
        const chatModels: AiModelItem[] = (modelsRes.data || []).filter((m: AiModelItem) => m.is_active && m.category === 'chat')
        setServerModels(chatModels)

        // Парсим calculated-prices для чата
        const cp: Record<string, {input: number, output: number}> = {}
        const raw = pricesRes.data || {}
        for (const [k, v] of Object.entries(raw)) {
          if (typeof v === 'object' && v && 'input' in v && 'output' in v && (v as any).unit === 'tokens') {
            cp[k] = {input: (v as any).input, output: (v as any).output}
          }
        }
        setCalculatedPrices(cp)

        // Ставим первую бесплатную как дефолт
        const free = chatModels.find(m => !m.is_paid)
        if (free) setSelectedModel(free.model_name)
        else if (chatModels.length > 0) setSelectedModel(chatModels[0].model_name)
      } catch { /* ignore */ }
    }
    load()
  }, [])

  // ---- Auto-translate ----
  const triggerTranslate = useCallback(async (text: string, model?: string) => {
    if (!text.trim()) {
      setTranslatedText('')
      return
    }
    setTranslating(true)
    try {
      const { data } = await client.post('/translate', {
        text,
        source_lang: sourceLang,
        target_lang: targetLang,
        model: model || selectedModel,
      })
      setTranslatedText(data.translated || '')
      if (data.detected_lang) {
        setDetectedLang(data.detected_lang)
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Ошибка перевода'
      if (!msg.includes('Не авторизован')) {
        toast.error(msg)
      }
    } finally {
      setTranslating(false)
    }
  }, [sourceLang, targetLang, selectedModel])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!sourceText.trim()) {
      setTranslatedText('')
      return
    }
    debounceRef.current = setTimeout(() => {
      triggerTranslate(sourceText)
    }, 600)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [sourceText, sourceLang, targetLang, selectedModel, triggerTranslate])

  // ---- Compress handler ----
  const handleCompress = async () => {
    if (!compressText.trim()) { toast.error('Введите текст для сжатия'); return }
    setCompressing(true)
    setCompressResult('')
    try {
      const { data } = await client.post('/translate/ai-process', {
        action: 'compress', text: compressText, model: selectedModel,
      })
      setCompressResult(data.text || '')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Ошибка сжатия')
    } finally { setCompressing(false) }
  }

  // ---- YouTube handler ----
  const handleYoutubeSummarize = async () => {
    if (!youtubeUrl.trim()) { toast.error('Введите URL YouTube видео'); return }
    setYoutubeLoading(true)
    setYoutubeResult('')
    try {
      const { data } = await client.post('/translate/ai-process', {
        action: 'youtube', url: youtubeUrl, model: selectedModel,
      })
      setYoutubeResult(data.text || '')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Ошибка обработки')
    } finally { setYoutubeLoading(false) }
  }

  // ---- Swap languages ----
  const swapLanguages = () => {
    if (sourceLang === 'auto') return
    setSourceLang(targetLang)
    setTargetLang(sourceLang)
    setSourceText(translatedText)
    setTranslatedText('')
    setDetectedLang(null)
  }

  // ---- Copy ----
  const copyToClipboard = async () => {
    if (!translatedText) return
    try {
      await navigator.clipboard.writeText(translatedText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success('Скопировано')
    } catch {
      toast.error('Не удалось скопировать')
    }
  }

  // ---- File upload ----
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    if (!['txt', 'pdf', 'docx'].includes(ext)) {
      toast.error('Поддерживаются только txt, pdf, docx')
      return
    }
    setUploadedFile({ name: file.name, size: file.size })
    setTranslating(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('target_lang', targetLang)
      formData.append('source_lang', sourceLang)
      const { data } = await client.post('/translate/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      // Store original file text
      const extractedText = data.translated || ''
      setUploadedFile(prev => prev ? { ...prev, originalText: extractedText } : null)
      setSourceText('')  // source не показываем (текст был в файле)
      setTranslatedText(extractedText)
      if (data.detected_lang) setDetectedLang(data.detected_lang)
      toast.success('Файл переведён')
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Ошибка при переводе файла'
      toast.error(msg)
    } finally {
      setTranslating(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const removeFile = () => {
    setUploadedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ---- History ----
  const loadHistory = async () => {
    setLoadingHistory(true)
    try {
      const { data } = await client.get('/translate/history')
      setHistory(data.translations || [])
      setShowHistory(true)
    } catch {
      toast.error('Не удалось загрузить историю')
    } finally {
      setLoadingHistory(false)
    }
  }

  const clearHistory = async () => {
    try {
      await client.delete('/translate/history')
      setHistory([])
      toast.success('История очищена')
    } catch {
      toast.error('Ошибка при очистке')
    }
  }

  const restoreFromHistory = (item: HistoryItem) => {
    setSourceText(item.source_text)
    setTranslatedText(item.translated_text || '')
    setSourceLang(item.source_lang)
    setTargetLang(item.target_lang)
    setShowHistory(false)
  }

  const currentModel = serverModels.find(m => m.model_name === selectedModel)
  const currentModelPrice = currentModel ? formatPrice(currentModel) : null
  const sourceLangName = SUPPORTED_LANGUAGES.find(l => l.code === (detectedLang || sourceLang))
  const targetLangName = SUPPORTED_LANGUAGES.find(l => l.code === targetLang)

  return (
    <div className="flex flex-col h-full bg-[#0f0f1a]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f0f1a]/95 backdrop-blur-sm border-b border-white/5 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Languages className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Переводчик</h1>
              <p className="text-xs text-gray-500">AI-перевод текста и файлов</p>
            </div>
          </div>
          <button
            onClick={() => showHistory ? setShowHistory(false) : loadHistory()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all text-xs"
          >
            <History className="w-3.5 h-3.5" />
            История
          </button>
        </div>
      </div>

      {/* ─── Tab buttons ─── */}
      <div className="flex gap-1 bg-white/[0.03] rounded-lg p-0.5 border border-white/5 mx-6 mt-3 shrink-0">
        {[
          { id: 'translate' as const, label: 'Перевод', icon: Languages },
          { id: 'compress' as const, label: 'Компрессор', icon: Scissors },
          { id: 'youtube' as const, label: 'YouTube', icon: Youtube },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white/10 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'translate' && (<>
        {showHistory ? (
          /* ---- History ---- */
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">История переводов</h2>
            <div className="flex items-center gap-2">
              {history.length > 0 && (
                <button onClick={clearHistory} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300">
                  <Trash2 className="w-3 h-3" />Очистить
                </button>
              )}
              <button onClick={() => setShowHistory(false)} className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300">
                ← Вернуться в Переводчик
              </button>
            </div>
          </div>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">История переводов пуста</div>
          ) : (
            <div className="space-y-2">
              {history.map((item) => (
                <button key={item.id} onClick={() => restoreFromHistory(item)} className="w-full text-left p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] transition-all group">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <span>{LANG_EMOJIS[item.source_lang] || '🌐'} → {LANG_EMOJIS[item.target_lang] || '🌐'}</span>
                    <span className="text-gray-600">·</span>
                    <span>{item.model_name || '—'}</span>
                    {item.file_name && (<><span className="text-gray-600">·</span><FileText className="w-3 h-3" /><span className="truncate max-w-[120px]">{item.file_name}</span></>)}
                  </div>
                  <p className="text-sm text-gray-300 line-clamp-2">{item.source_text}</p>
                  {item.translated_text && <p className="text-sm text-gray-400 line-clamp-2 mt-1 pl-3 border-l border-white/10">{item.translated_text}</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* ---- Main ---- */
        <div className="flex-1 flex flex-col p-4 gap-4">

          {/* Toolbar: Language selectors + Model picker */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Source language — smaller */}
            <div className="relative shrink-0">
              <button
                onClick={() => setShowSourcePicker(!showSourcePicker)}
                className="input-field text-sm flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <span className="text-xs">{LANG_EMOJIS[detectedLang || sourceLang] || '🌐'}</span>
                <span className="text-xs max-w-[70px] truncate">
                  {sourceLang === 'auto' ? 'Авто' : (SUPPORTED_LANGUAGES.find(l => l.code === (detectedLang || sourceLang))?.name || 'Исходный')}
                </span>
                <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
              </button>
              {showSourcePicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSourcePicker(false)} />
                  <div className="absolute top-full left-0 mt-1 w-48 glass-card z-20 p-1.5 space-y-0.5 max-h-56 overflow-y-auto">
                    {SUPPORTED_LANGUAGES.map((l) => (
                      <button key={l.code}
                        onClick={() => { setSourceLang(l.code); setDetectedLang(null); setShowSourcePicker(false) }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${(detectedLang || sourceLang) === l.code ? 'bg-emerald-500/10 text-emerald-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}
                      >
                        <span className="mr-1.5">{LANG_EMOJIS[l.code] || '🌐'}</span>{l.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button
              onClick={swapLanguages}
              disabled={sourceLang === 'auto' || !translatedText}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-emerald-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              title="Поменять языки"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
            </button>

            {/* Target language — smaller */}
            <div className="relative shrink-0">
              <button
                onClick={() => setShowTargetPicker(!showTargetPicker)}
                className="input-field text-sm flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <span className="text-xs">{LANG_EMOJIS[targetLang] || '🌐'}</span>
                <span className="text-xs max-w-[70px] truncate">{SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name || 'Перевод'}</span>
                <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
              </button>
              {showTargetPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowTargetPicker(false)} />
                  <div className="absolute top-full left-0 mt-1 w-48 glass-card z-20 p-1.5 space-y-0.5 max-h-56 overflow-y-auto">
                    {SUPPORTED_LANGUAGES.filter(l => l.code !== 'auto').map((l) => (
                      <button key={l.code}
                        onClick={() => { setTargetLang(l.code); setShowTargetPicker(false) }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${targetLang === l.code ? 'bg-emerald-500/10 text-emerald-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}
                      >
                        <span className="mr-1.5">{LANG_EMOJIS[l.code] || '🌐'}</span>{l.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Model picker — на мобилке на новой строке */}
            <div className="relative shrink-0 w-full sm:w-auto">
              <div className="block sm:hidden w-full h-[1px]" />
              <button
                onClick={() => setShowModelPicker(!showModelPicker)}
                className="input-field text-sm flex items-center gap-1.5 cursor-pointer whitespace-nowrap py-[9px]"
              >
                <Sparkles className="w-3 h-3 text-indigo-400" />
                <span className="text-xs max-w-[80px] truncate">{currentModel?.display_name || currentModel?.model_name || 'Модель'}</span>
                <span className="text-[9px] text-indigo-400/60">{currentModel?.is_paid ? '👑' : 'FREE'}</span>
                {currentModelPrice && (
                  <span className="text-[9px] text-gray-500">{currentModelPrice}</span>
                )}
                <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
              </button>
              {showModelPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowModelPicker(false)} />
                  <div className="absolute top-full left-0 mt-1 w-64 glass-card z-20 p-1.5 max-h-64 overflow-y-auto">
                    {/* Бесплатные */}
                    {sortedModels.filter(m => !m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-emerald-400/70 uppercase tracking-wider">Бесплатные</div>
                        {sortedModels.filter(m => !m.is_paid).map((m) => (
                          <button key={m.model_name}
                            onClick={() => { setSelectedModel(m.model_name); setShowModelPicker(false) }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}
                          >
                            <Sparkles className="w-3 h-3 shrink-0 text-emerald-400/60" />
                            <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                            <span className="text-[9px] text-emerald-400/50 shrink-0">FREE</span>
                          </button>
                        ))}
                      </>
                    )}
                    {/* Платные */}
                    {sortedModels.filter(m => m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-amber-400/70 uppercase tracking-wider mt-1">Платные</div>
                        {sortedModels.filter(m => m.is_paid).map((m) => {
                          const price = formatPrice(m)
                          return (
                            <button key={m.model_name}
                              onClick={() => {
                                if (isPaidLocked) { toast.error('Недостаточно токенов'); return }
                                setSelectedModel(m.model_name); setShowModelPicker(false)
                              }}
                              className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'} ${isPaidLocked ? 'opacity-40 cursor-not-allowed' : ''}`}
                            >
                              {isPaidLocked && <Lock className="w-3 h-3 shrink-0 text-amber-400/60" />}
                              {!isPaidLocked && <Sparkles className="w-3 h-3 shrink-0 text-indigo-400/60" />}
                              <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                              {price && <span className="text-[9px] text-gray-500 shrink-0">{price}</span>}
                              <span className="text-[9px] text-amber-400/50 shrink-0">👑</span>
                            </button>
                          )
                        })}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Spacer */}
            <div className="flex-1" />
          </div>

          {/* Dual Panels */}
          <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0">
            {/* Source */}
            <div className="flex-1 flex flex-col bg-white/[0.03] border border-white/5 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-400">
                    {LANG_EMOJIS[detectedLang || sourceLang] || ''} {sourceLangName?.name || 'Исходный'}
                  </span>
                  {detectedLang && sourceLang === 'auto' && (
                    <span className="text-[10px] text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                      {SUPPORTED_LANGUAGES.find(l => l.code === detectedLang)?.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input ref={fileInputRef} type="file" accept=".txt,.pdf,.docx" onChange={handleFileUpload} className="hidden" />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={translating}
                    className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-300 transition-all disabled:opacity-50"
                    title="Загрузить файл (txt, pdf, docx)"
                  >
                    <Upload className="w-3 h-3" />Файл
                  </button>
                  <button
                    onClick={() => { setSourceText(''); setDetectedLang(null); setTranslatedText('') }}
                    disabled={!sourceText.trim()}
                    className="p-1 rounded text-gray-500 hover:text-red-400 transition-all disabled:opacity-30"
                    title="Очистить"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(sourceText); toast.success('Скопировано') }}
                    disabled={!sourceText.trim()}
                    className="p-1 rounded text-gray-500 hover:text-gray-300 transition-all disabled:opacity-30"
                    title="Копировать исходный текст"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  <button
                    onClick={handleVoiceInput}
                    disabled={translating}
                    className={`p-1 rounded transition-all ${listening ? 'text-red-400 bg-red-500/10' : 'text-gray-500 hover:text-gray-300'}`}
                    title={listening ? 'Остановить запись' : 'Надиктовать текст'}
                  >
                    <Mic className="w-3 h-3" />
                  </button>
                  <span className="text-[10px] text-gray-600">{sourceText.length}/5000</span>
                </div>
              </div>
              <textarea
                ref={textareaRef}
                value={sourceText}
                onChange={(e) => { if (e.target.value.length <= 5000) setSourceText(e.target.value) }}
                placeholder="Введите текст для перевода или загрузите файл..."
                className="flex-1 bg-transparent text-sm text-gray-200 placeholder-gray-600 resize-none outline-none p-4 leading-relaxed"
              />
              {uploadedFile && (
                <div className="flex items-center gap-2 px-4 py-2 border-t border-white/5 bg-white/[0.02]">
                  <FileText className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-xs text-gray-400 truncate flex-1">{uploadedFile.name}</span>
                  <span className="text-[10px] text-gray-600">{(uploadedFile.size / 1024).toFixed(0)} KB</span>
                  {translatedText && (
                    <button
                      onClick={() => {
                        const blob = new Blob([translatedText], { type: 'text/plain;charset=utf-8' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = uploadedFile.name.replace(/\.\w+$/, '') + '_translated.txt'
                        a.click()
                        URL.revokeObjectURL(url)
                      }}
                      className="text-gray-500 hover:text-emerald-400"
                      title="Скачать перевод"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                  )}
                  <button onClick={removeFile} className="text-gray-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>

            {/* Result */}
            <div className="flex-1 flex flex-col bg-white/[0.03] border border-white/5 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
                <span className="text-xs font-medium text-gray-400">
                  {LANG_EMOJIS[targetLang] || ''} {targetLangName?.name || 'Перевод'}
                </span>
                <div className="flex items-center gap-2">
                  {translating && <Loader2 className="w-3.5 h-3.5 text-emerald-400 animate-spin" />}
                  {/* Всегда видимые кнопки */}
                  {/* Voice + Stop */}
                  <div className="relative flex items-center">
                    <button
                      onClick={handleSpeak}
                      className={`p-1 rounded transition-all ${speaking ? 'text-red-400 bg-red-500/10' : 'text-gray-500 hover:text-emerald-400'}`}
                      title={speaking ? 'Стоп' : 'Озвучить перевод'}
                    >
                      {speaking ? <StopCircle className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => setShowVoicePicker(!showVoicePicker)}
                      className="p-0.5 rounded text-gray-600 hover:text-gray-400 transition-all"
                      title="Выбрать голос"
                    >
                      <ChevronDown className="w-2.5 h-2.5" />
                    </button>
                    {showVoicePicker && availableVoices.length > 0 && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowVoicePicker(false)} />
                        <div className="absolute top-full right-0 mt-1 w-64 glass-card z-20 p-1.5 max-h-64 overflow-y-auto">
                          {Object.entries(voicesGrouped).map(([code, groupVoices]) => (
                            <div key={code}>
                              <div className="px-2.5 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{code.toUpperCase()}</div>
                              {groupVoices.map((v) => (
                                <button key={v.name}
                                  onClick={() => {
                                    setSelectedVoiceUri(v.name)
                                    setShowVoicePicker(false)
                                    speakText(translatedText, v.name)
                                  }}
                                  className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${selectedVoiceUri === v.name ? 'bg-emerald-500/10 text-emerald-300' : 'text-gray-400 hover:bg-white/5'}`}
                                >
                                  <Volume2 className="w-3 h-3 shrink-0 text-emerald-400/60" />
                                  <span className="truncate">{v.friendly_name || v.name}</span>
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Копировать */}
                  <button
                    onClick={copyToClipboard}
                    className="p-1 rounded text-gray-500 hover:text-emerald-400 transition-all"
                    title="Копировать"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  {/* Очистить */}
                  <button
                    onClick={() => { setTranslatedText('') }}
                    disabled={!translatedText}
                    className="p-1 rounded text-gray-500 hover:text-red-400 transition-all disabled:opacity-30"
                    title="Очистить"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {translating && !translatedText ? (
                  <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 text-emerald-400 animate-spin" /></div>
                ) : translatedText ? (
                  <div className="text-sm text-gray-200 leading-relaxed prose prose-invert max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{translatedText}</ReactMarkdown></div>
                ) : (
                  <p className="text-sm text-gray-600">Перевод появится здесь...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* ───────────────── COMPRESS TAB ───────────────── */}
      {activeTab === 'compress' && (
        <div className="flex-1 flex flex-col p-4 gap-4">
          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/5">
              <Scissors className="w-4 h-4 text-purple-400" />
              <span className="text-xs font-medium text-gray-300">Компрессор текста</span>
              <span className="text-[10px] text-gray-500">| Сжатие в 2-3 раза без потери смысла</span>
            </div>
            <div className="relative shrink-0">
              <button
                onClick={() => setShowModelPicker(!showModelPicker)}
                className="input-field text-sm flex items-center gap-1.5 cursor-pointer whitespace-nowrap py-[9px]"
              >
                <Sparkles className="w-3 h-3 text-indigo-400" />
                <span className="text-xs max-w-[80px] truncate">{currentModel?.display_name || currentModel?.model_name || 'Модель'}</span>
                <span className="text-[9px] text-indigo-400/60">{currentModel?.is_paid ? '👑' : 'FREE'}</span>
                {currentModelPrice && (
                  <span className="text-[9px] text-gray-500">{currentModelPrice}</span>
                )}
                <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
              </button>
              {showModelPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowModelPicker(false)} />
                  <div className="absolute top-full left-0 mt-1 w-64 glass-card z-20 p-1.5 max-h-64 overflow-y-auto">
                    {/* Бесплатные */}
                    {sortedModels.filter(m => !m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-emerald-400/70 uppercase tracking-wider">Бесплатные</div>
                        {sortedModels.filter(m => !m.is_paid).map((m) => (
                          <button key={m.model_name}
                            onClick={() => { setSelectedModel(m.model_name); setShowModelPicker(false) }}
                            className={"w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 " + (selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200')}
                          >
                            <Sparkles className="w-3 h-3 shrink-0 text-emerald-400/60" />
                            <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                            <span className="text-[9px] text-emerald-400/50 shrink-0">FREE</span>
                          </button>
                        ))}
                      </>
                    )}
                    {/* Платные */}
                    {sortedModels.filter(m => m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-amber-400/70 uppercase tracking-wider mt-1">Платные</div>
                        {sortedModels.filter(m => m.is_paid).map((m) => {
                          const price = formatPrice(m)
                          return (
                            <button key={m.model_name}
                              onClick={() => {
                                if (isPaidLocked) { toast.error('Недостаточно токенов'); return }
                                setSelectedModel(m.model_name); setShowModelPicker(false)
                              }}
                              className={"w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 " + (selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200') + (isPaidLocked ? ' opacity-40 cursor-not-allowed' : '')}
                            >
                              {isPaidLocked && <Lock className="w-3 h-3 shrink-0 text-amber-400/60" />}
                              {!isPaidLocked && <Sparkles className="w-3 h-3 shrink-0 text-indigo-400/60" />}
                              <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                              {price && <span className="text-[9px] text-gray-500 shrink-0">{price}</span>}
                              <span className="text-[9px] text-amber-400/50 shrink-0">👑</span>
                            </button>
                          )
                        })}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Two-panel layout */}
          <div className="flex-1 flex gap-4 min-h-0">
            {/* Source */}
            <div className="flex-1 flex flex-col bg-white/[0.03] border border-white/5 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
                <span className="text-xs font-medium text-gray-400">Исходный текст</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => { navigator.clipboard.writeText(compressText); toast.success('Скопировано') }}
                    disabled={!compressText}
                    className="p-1 rounded text-gray-500 hover:text-gray-300 transition-all disabled:opacity-30"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => { setCompressText(''); setCompressResult('') }}
                    disabled={!compressText}
                    className="p-1 rounded text-gray-500 hover:text-red-400 transition-all disabled:opacity-30"
                    title="Очистить"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <textarea
                ref={compressTextareaRef}
                value={compressText}
                onChange={e => setCompressText(e.target.value)}
                placeholder="Введите текст для сжатия…"
                className="flex-1 bg-transparent text-sm text-gray-200 placeholder-gray-600 resize-none outline-none p-4 leading-relaxed"
              />
              <div className="flex items-center justify-between px-4 py-2 border-t border-white/5 bg-white/[0.02]">
                <span className="text-[10px] text-gray-600">
                  {compressText.length} зн. {(() => { try { return compressText.match(/\S+/g)?.length || 0 } catch { return 0 } })()} сл.
                </span>
                <button
                  onClick={handleCompress}
                  disabled={!compressText.trim() || compressing}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 hover:text-purple-200 transition-all text-xs font-medium disabled:opacity-40"
                >
                  {compressing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Сжимаю…</> : <><Scissors className="w-3.5 h-3.5" /> Сжать</>}
                </button>
              </div>
            </div>

            {/* Result */}
            <div className="flex-1 flex flex-col bg-white/[0.03] border border-white/5 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-400">Результат</span>
                  {compressResult && (
                    <span className="text-[10px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full">
                      {(() => { const pct = Math.round((1 - compressResult.length / compressText.length) * 100); return <>{pct}% сжатия</> })()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Voice */}
                  <div className="relative flex items-center">
                    <button
                      onClick={() => { if (compressResult) speakText(compressResult, selectedVoiceUri || '') }}
                      className="p-1 rounded transition-all text-gray-500 hover:text-emerald-400"
                      title="Озвучить"
                    >
                      <Volume2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setShowVoicePicker(!showVoicePicker)}
                      className="p-0.5 rounded text-gray-600 hover:text-gray-400 transition-all"
                    >
                      <ChevronDown className="w-2.5 h-2.5" />
                    </button>
                    {showVoicePicker && availableVoices.length > 0 && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowVoicePicker(false)} />
                        <div className="absolute top-full right-0 mt-1 w-64 glass-card z-20 p-1.5 max-h-64 overflow-y-auto">
                          {Object.entries(voicesGrouped).map(([code, groupVoices]) => (
                            <div key={code}>
                              <div className="px-2.5 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{code.toUpperCase()}</div>
                              {groupVoices.map((v) => (
                                <button key={v.name}
                                  onClick={() => {
                                    setSelectedVoiceUri(v.name);
                                    setShowVoicePicker(false);
                                    speakText(compressResult, v.name);
                                  }}
                                  className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${selectedVoiceUri === v.name ? 'bg-emerald-500/10 text-emerald-300' : 'text-gray-400 hover:bg-white/5'}`}
                                >
                                  <Volume2 className="w-3 h-3 shrink-0 text-emerald-400/60" />
                                  <span className="truncate">{v.friendly_name || v.name}</span>
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(compressResult); toast.success('Скопировано') }}
                    disabled={!compressResult}
                    className="p-1 rounded text-gray-500 hover:text-emerald-400 transition-all disabled:opacity-30"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {compressing ? (
                  <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 text-purple-400 animate-spin" /></div>
                ) : compressResult ? (
                  <div className="text-sm text-gray-200 leading-relaxed">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {compressResult}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Сжатый текст появится здесь…</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────── YOUTUBE TAB ───────────────── */}
      {activeTab === 'youtube' && (
        <div className="flex-1 flex flex-col p-4 gap-4">
          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/5">
              <Youtube className="w-4 h-4 text-red-400" />
              <span className="text-xs font-medium text-gray-300">YouTube суммаризатор</span>
              <span className="text-[10px] text-gray-500">| Краткое содержание видео</span>
            </div>
            <div className="relative shrink-0">
              <button
                onClick={() => setShowModelPicker(!showModelPicker)}
                className="input-field text-sm flex items-center gap-1.5 cursor-pointer whitespace-nowrap py-[9px]"
              >
                <Sparkles className="w-3 h-3 text-indigo-400" />
                <span className="text-xs max-w-[80px] truncate">{currentModel?.display_name || currentModel?.model_name || 'Модель'}</span>
                <span className="text-[9px] text-indigo-400/60">{currentModel?.is_paid ? '👑' : 'FREE'}</span>
                {currentModelPrice && (
                  <span className="text-[9px] text-gray-500">{currentModelPrice}</span>
                )}
                <ChevronDown className="w-3 h-3 text-gray-500 shrink-0" />
              </button>
              {showModelPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowModelPicker(false)} />
                  <div className="absolute top-full left-0 mt-1 w-64 glass-card z-20 p-1.5 max-h-64 overflow-y-auto">
                    {/* Бесплатные */}
                    {sortedModels.filter(m => !m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-emerald-400/70 uppercase tracking-wider">Бесплатные</div>
                        {sortedModels.filter(m => !m.is_paid).map((m) => (
                          <button key={m.model_name}
                            onClick={() => { setSelectedModel(m.model_name); setShowModelPicker(false) }}
                            className={"w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 " + (selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200')}
                          >
                            <Sparkles className="w-3 h-3 shrink-0 text-emerald-400/60" />
                            <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                            <span className="text-[9px] text-emerald-400/50 shrink-0">FREE</span>
                          </button>
                        ))}
                      </>
                    )}
                    {/* Платные */}
                    {sortedModels.filter(m => m.is_paid).length > 0 && (
                      <>
                        <div className="px-2.5 py-1 text-[10px] font-semibold text-amber-400/70 uppercase tracking-wider mt-1">Платные</div>
                        {sortedModels.filter(m => m.is_paid).map((m) => {
                          const price = formatPrice(m)
                          return (
                            <button key={m.model_name}
                              onClick={() => {
                                if (isPaidLocked) { toast.error('Недостаточно токенов'); return }
                                setSelectedModel(m.model_name); setShowModelPicker(false)
                              }}
                              className={"w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 " + (selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200') + (isPaidLocked ? ' opacity-40 cursor-not-allowed' : '')}
                            >
                              {isPaidLocked && <Lock className="w-3 h-3 shrink-0 text-amber-400/60" />}
                              {!isPaidLocked && <Sparkles className="w-3 h-3 shrink-0 text-indigo-400/60" />}
                              <span className="truncate flex-1">{m.display_name || m.model_name}</span>
                              {price && <span className="text-[9px] text-gray-500 shrink-0">{price}</span>}
                              <span className="text-[9px] text-amber-400/50 shrink-0">👑</span>
                            </button>
                          )
                        })}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* URL input */}
          <div className="flex items-center gap-2">
            <input
              value={youtubeUrl}
              onChange={e => setYoutubeUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="flex-1 bg-white/[0.03] border border-white/5 rounded-lg px-4 py-2 text-sm text-gray-200 placeholder-gray-600 outline-none focus:border-red-500/30 transition-colors"
            />
            <button
              onClick={() => { setYoutubeUrl(''); setYoutubeResult('') }}
              disabled={!youtubeUrl}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-red-400 transition-all disabled:opacity-30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Options */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowYoutubeLangPicker(!showYoutubeLangPicker)}
                className="input-field text-xs flex items-center gap-1.5 cursor-pointer"
              >
                <Languages className="w-3 h-3 text-gray-400" />
                <span>{YOUTUBE_LANGS.find(l => l.code === youtubeLang)?.name || 'Русский'}</span>
                <ChevronDown className="w-3 h-3 text-gray-500" />
              </button>
              {showYoutubeLangPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowYoutubeLangPicker(false)} />
                  <div className="absolute top-full left-0 mt-1 w-40 glass-card z-20 p-1.5 space-y-0.5">
                    {YOUTUBE_LANGS.map(l => (
                      <button key={l.code}
                        onClick={() => { setYoutubeLang(l.code); setShowYoutubeLangPicker(false) }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${youtubeLang === l.code ? 'bg-red-500/10 text-red-300' : 'text-gray-400 hover:bg-white/5'}`}
                      >
                        {l.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={handleYoutubeSummarize}
              disabled={!youtubeUrl.trim() || youtubeLoading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 hover:text-red-200 transition-all text-xs font-medium disabled:opacity-40"
            >
              {youtubeLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Анализирую…</> : <><Youtube className="w-4 h-4" /> Суммаризировать</>}
            </button>
          </div>

          {/* Result */}
          <div className="flex-1 flex flex-col bg-white/[0.03] border border-white/5 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
              <span className="text-xs font-medium text-gray-400">Содержание видео</span>
              <div className="flex items-center gap-1.5">
                {youtubeResult && (
                  <>
                    <div className="relative flex items-center">
                      <button
                        onClick={() => { if (youtubeResult) speakText(youtubeResult, selectedVoiceUri || '') }}
                        className="p-1 rounded transition-all text-gray-500 hover:text-emerald-400"
                        title="Озвучить"
                      >
                        <Volume2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setShowVoicePicker(!showVoicePicker)}
                        className="p-0.5 rounded text-gray-600 hover:text-gray-400 transition-all"
                      >
                        <ChevronDown className="w-2.5 h-2.5" />
                      </button>
                      {showVoicePicker && availableVoices.length > 0 && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowVoicePicker(false)} />
                          <div className="absolute top-full right-0 mt-1 w-64 glass-card z-20 p-1.5 max-h-64 overflow-y-auto">
                            {Object.entries(voicesGrouped).map(([code, groupVoices]) => (
                              <div key={code}>
                                <div className="px-2.5 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{code.toUpperCase()}</div>
                                {groupVoices.map((v) => (
                                  <button key={v.name}
                                    onClick={() => {
                                      setSelectedVoiceUri(v.name);
                                      setShowVoicePicker(false);
                                      speakText(youtubeResult, v.name);
                                    }}
                                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-2 ${selectedVoiceUri === v.name ? 'bg-emerald-500/10 text-emerald-300' : 'text-gray-400 hover:bg-white/5'}`}
                                  >
                                    <Volume2 className="w-3 h-3 shrink-0 text-emerald-400/60" />
                                    <span className="truncate">{v.friendly_name || v.name}</span>
                                  </button>
                                ))}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(youtubeResult); toast.success('Скопировано') }}
                      className="p-1 rounded text-gray-500 hover:text-emerald-400 transition-all"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {youtubeLoading ? (
                <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 text-red-400 animate-spin" /></div>
              ) : youtubeResult ? (
                <div className="text-sm text-gray-200 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {youtubeResult}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Вставьте ссылку на YouTube видео и нажмите «Суммаризировать»…</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
